import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from '@thd-olt-component-react/router';
import { Pagination } from '@thd-olt-component-react/pagination';
import { Col, Image, Row } from '@thd-olt-component-react/core-ui';
import { useStore } from '../../../../store';
import ProfileGridItem from '../../../../components/ProfileSearch/ProfileGridItem/ProfileGridItem';
import { Spinner } from '../../../../components/common/Spinner/Spinner';
import { JOB_POSTING_STATUS } from '../../../../utils/constants';
import useQuery from '../../../../utils/useQuery';
import ErrorAlert from '../../../../components/common/ErrorAlert/ErrorAlert';
import InviteCandidateButton from './InviteCandidateButton';
import { clickEvents } from '../../../../utils/analytics';
import { neighborCalc } from '../../../../utils/paginationUtils';
import { generateImageSource } from '../../../../utils/generateImageSource';

const NO_CANDIDATES_MESSAGE = {
  ACTIVE: (
    <>
      At this time, there are no available candidates who meet your exact job posting requirements,
      based on their trade interests, location, and years of experience. However, many candidates
      are eager to learn, and could be a good fit for your company, even if they are missing certain
      skills. Please feel free to browse candidates here.
    </>
  ),
  DRAFT: (
    <>
      When we publish your job, we will recommend candidates we think would be a great fit for your
      job requirements, based on their trade interests, location, and years of experience.
    </>
  ),
  COMPLETED: (
    <>
      Path to Pro Network will only recommend candidates for active job postings.
      <br />
      This posting is complete, so there are no recommended candidates at this time.
    </>
  ),
  CANCELLED: (
    <>
      Path to Pro Network will only recommend candidates for active job postings.
      <br />
      This posting is canceled, so there are no recommended candidates at this time.
    </>
  )
};

export const RecommendedCandidates = () => {
  const query = useQuery();

  return <RecommendedCandidatesList pageParam={query.get('page')} />;
};

const RecommendedCandidatesList = observer(({ pageParam }) => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const query = useQuery();

  const {
    recommendedCandidatesStore: {
      candidates,
      fetchRecommendedCandidates,
      flushRecommendedCandidates,
      page,
      setPage,
      isLoading,
      paginationInfo
    },
    jobPostingStore: { activeJobPosting }
  } = useStore();

  useEffect(() => {
    if (activeJobPosting?.status === JOB_POSTING_STATUS.ACTIVE) {
      fetchRecommendedCandidates(activeJobPosting.jobPostingId);
      clickEvents.recommendationsTabClick('recommendations tab');
    } else {
      flushRecommendedCandidates();
      clickEvents.recommendationsTabClick('recommendations tab>none');
    }

  }, [activeJobPosting?.jobPostingId, page, activeJobPosting.status]);

  useEffect(() => {
    pageParam && setPage(pageParam);
  }, [pageParam]);

  const handlePageChange = (p) => {
    sectionRef.current.scrollIntoView();
    query.set('page', p);
    navigate({ search: query.toString() });
  };

  const renderNoCandidateMessage = (status) => {
    if (NO_CANDIDATES_MESSAGE[status] !== undefined) {
      return NO_CANDIDATES_MESSAGE[status];
    }

    return NO_CANDIDATES_MESSAGE.ACTIVE;
  };

  return (
    <>
      <ErrorAlert />
      {isLoading && <Spinner />}

      {!isLoading && (
        <Row>
          <Col sm={12} xs={12}>
            <div className="ptp-job-posting__sub-title">
              <Image
                src={generateImageSource('/images/v1/path-to-pro/reccomended-candidates-icon.svg')}
                className="ptp-recommended-page__recommended-candidates-icon"
              />
              Recommended Candidates
            </div>
          </Col>
        </Row>
      )}

      {!isLoading && !candidates?.length && (
        <div className="ptp-recommended-page__description" data-testid="job-posting-recommended-no-results-message">
          {renderNoCandidateMessage(activeJobPosting?.status)}
        </div>
      )}

      {!isLoading && !!candidates?.length && (
        <section ref={sectionRef}>
          <div className="ptp-recommended-page__description">
            Recommended candidates are candidates we think would be a great fit for your job, based
            on their trade interests, location, and years of experience. Click to invite candidates
            to apply to your job! Candidates who receive an invitation are encouraged to submit an
            application to your job.
          </div>
          <CandidatesList candidates={candidates} />
          <div className="slm__pagination_container" data-testid="job-applicants-pagination">
            <Pagination
              showArrows
              pagesBeforeDotsShow={3}
              totalPages={paginationInfo.totalPages}
              currentPage={page}
              onPageChanged={handlePageChange}
              rightNeighbors={neighborCalc(paginationInfo.totalPages, page)}
            />
          </div>
        </section>
      )}
    </>
  );
});

export const CandidatesList = ({ candidates = [] }) => {
  return (
    <div className="ptp-recommended-page__candidates-list" data-testid="job-posting-recommend-list">
      {candidates.map((item, ind) => (
        <div key={ind} className="ptp-recommended-page__card" data-testid="job-posting-recommend-card">
          <ProfileGridItem key={item.candidateId} candidate={item} />
          <InviteCandidateButton
            allowInvite={(item.status === null)}
            candidateId={item.candidateId}
          />
        </div>
      ))}
    </div>
  );
};
